import { zodResolver } from '@hookform/resolvers/zod';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { useEffect, useState } from 'react';
import { useForm, useController, useFormState, Controller, SubmitHandler } from 'react-hook-form';
import MaskedInput, { conformToMask } from 'react-text-mask';
import { z } from 'zod';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import CloseSvg from '@/components/common/CloseSvg';
import HidePasswordSvg from '@/components/common/HidePasswordSvg';
import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import InfoTooltipSvgWhite from '@/components/common/InfoTooltipSvgWhite';
import LoadingSvg from '@/components/common/LoadingSvg';
import { Popover, PopoverArrow, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/common/popover';
import ViewPasswordSvg from '@/components/common/ViewPasswordSvg';
import {
    isValidContainNumber,
    isValidTooSmall,
    isValidSpecial,
    isValidLower,
    isValidUpper,
    isValidUpperLower,
} from '@/components/myaccount/utils/validation';
import { Button } from '@/components/ui/button';
import { TmAccountRequest } from '@/tscapi/model/request/account-request';
import { Cookie } from '@/tscapi/utils/cookie';

import { formatCreateAddress, convertToBoolean } from '../../myaccount/utils/address.utils';
import { useCreatePhoneAccount } from '../hooks/useCreatePhoneAccount';
import { AccountSchema } from '../schemas/account.schema';

declare let grecaptcha: any;
declare let reCaptchaEnabled: any;
interface CreatePhoneAccountProps {
    name: string;
}
type ValidationAccountSchema = z.infer<typeof AccountSchema>;
function upperCasePipe(conformedValue) {
    return conformedValue.toUpperCase();
}

export const CreatePhoneAccount = ({ name }: CreatePhoneAccountProps) => {
    const mutation = useCreatePhoneAccount();
    const isMutating = useIsMutating();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);
    const [formError, setFormError] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const toggleShowNewPassword = () => setShowNewPassword((prevState) => !prevState);
    const toggleShowRetypePassword = () => setShowRetypePassword((prevState) => !prevState);
    const [isWhite, setIsWhite] = useState(true);
    const handleStateChange = (e: boolean) => {
        setIsWhite(!e);
    };
    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        trigger,
        setValue,
        clearErrors,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields },
    } = useForm<ValidationAccountSchema>({
        resolver: zodResolver(AccountSchema),
        mode: 'all',
        criteriaMode: 'all',
        defaultValues: {
            customernumber: '',
            phone: '',
            email: '',
            subscribe: false,
            newPassword: '',
            retypeNewPassword: '',
            IsPhoneCustomer: true,
            IsGuest: false,
        },
    });
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    useEffect(() => {
        const loadReCaptcha = () => {
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + reCaptchaSiteKey;
            document.body.appendChild(script);
        };
        loadReCaptcha();
    }, []);

    const passw = watch('retypeNewPassword');
    const onSubmit: SubmitHandler<ValidationAccountSchema> = (data) => {
        if (hasSubmitted) return;

        setHasSubmitted(true);
        grecaptcha.ready(function () {
            grecaptcha
                .execute(reCaptchaSiteKey, { action: 'createphoneaccount' })
                .then((token) => {
                    const extraAccountPayload = {
                        CustomerNumber: data.customernumber,
                        Phone: data.phone.trim().replace(/[-()\s]/g, ''),
                        Email: data.email,
                        Subscribe: convertToBoolean(data.subscribe),
                        Password: data.newPassword,
                        RetypedPassword: data.retypeNewPassword,
                        ReCaptchaToken: token,
                        IsPhoneCustomer: convertToBoolean(data.IsPhoneCustomer),
                        IsGuest: convertToBoolean(data.IsGuest),
                    };
                    const formData = { ...extraAccountPayload };
                    //const formData1 = Object.assign(addressPayload, extraAccountPayload);
                    mutation.mutate(formData, {
                        onSuccess: () => {
                            const cookie: Cookie = new Cookie();
                            cookie.deleteTargetUrl();
                            window.location.href = '/pages/myaccount';
                        },
                        onError: (error) => {
                            setFormError(error as string);
                            setHasSubmitted(false);
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                        },
                    });
                })
                .catch(() => {
                    setHasSubmitted(false);
                });
        });

        //handleFormSubmit(formData);
    };

    return (
        <div className="px-5 pb-32 pt-9 md:mx-auto md:max-w-[580px]">
            {name}
            <h1 className="m-0 pb-2 text-xl font-bold text-[#1a1a1a] md:pb-4 md:text-center md:text-4xl">
                Transfer your Phone Account
            </h1>
            <p className="mb-4 text-base font-light">
                If you&apos;ve already shopped with us by phone, it&apos;s easy to register your account online. Simply
                enter your customer number and phone number below.
            </p>
            {formError && (
                <div className="notification__red x relative mt-4 flex items-start border-2 border-solid border-red-600 py-5 pl-2 pr-7 text-sm">
                    <img
                        src="/App_Assets/public/icon-exclamation-black.svg"
                        alt="exclamation-icon"
                        className="exclamation-img mr-2 max-w-[20px]"
                    />
                    <div>
                        <strong className="">{formError}</strong>
                    </div>
                </div>
            )}
            {/* --------- customer number --------- */}
            <div className="mb-5 mt-10 text-xs font-light">Required fields are marked with an asterisk (*)</div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 gap-0">
                    <div className="relative z-0 mb-6">
                        <Controller
                            name="customernumber"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <MaskedInput
                                        mask={[/[0-9]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                                        {...field}
                                        data-error={getFieldState('customernumber').error ? 'true' : 'false'}
                                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                                        guide={false}
                                        placeholder=" "
                                        tabIndex={0}
                                        type="text"
                                    />
                                    <label
                                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-sm font-light duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                        htmlFor="customernumber"
                                    >
                                        Customer number*
                                    </label>
                                    {errors.customernumber && (
                                        <div className="mt-1 text-xs text-[#df0c0c]">
                                            {errors.customernumber.message}
                                        </div>
                                    )}
                                    <p className="mb-7 mt-5 text-sm font-light">
                                        Your 10-digit cusomter number can be found on a previous order form.
                                    </p>
                                    <img
                                        src="/tsc/en_CA/Assets/images/poform.jpg"
                                        alt="number-location"
                                        className="w-4/6"
                                    ></img>
                                    <p className="mb-0 mt-4 text-sm font-light">
                                        Still don&apos;t know your customer number? Please call <b>1-888-2020-888</b>
                                    </p>
                                </>
                            )}
                        />
                    </div>
                </div>
                {/* --------- phone number --------- */}
                <div className="relative z-0 mb-[22px]">
                    <Controller
                        name="phone"
                        control={control}
                        // defaultValue={selectedCreditCard?.MaskedNumber || ''}
                        render={({ field, fieldState }) => (
                            <>
                                <div className="relative">
                                    <MaskedInput
                                        mask={[
                                            '(',
                                            /[1-9]/,
                                            /\d/,
                                            /\d/,
                                            ')',
                                            ' ',
                                            /\d/,
                                            /\d/,
                                            /\d/,
                                            '-',
                                            /\d/,
                                            /\d/,
                                            /\d/,
                                            /\d/,
                                        ]}
                                        {...field}
                                        data-error={getFieldState('phone').error ? 'true' : 'false'}
                                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 pb-0.5 pt-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                        guide={false}
                                        placeholder=" "
                                        autoComplete="billing tel"
                                        onFocus={() => {
                                            // debugger;
                                            // if (!getFieldState('dayphone').isDirty && field.value !== '') {
                                            //     setValue('dayphone', '');
                                            // }
                                        }}
                                        onChange={(event) => {
                                            field.onChange(event);
                                            if (errors.phone) {
                                                clearErrors('phone');
                                            }
                                        }}
                                        tabIndex={0}
                                        type="tel"
                                        data-qm-encrypt="true"
                                    />
                                    <label
                                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-sm font-light duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                                        htmlFor="phone"
                                    >
                                        Phone number*
                                    </label>
                                    <div className="absolute inset-y-0 right-0 flex items-center">
                                        <Popover placement="left">
                                            <PopoverTrigger
                                                className="popover__button"
                                                onStateChange={handleStateChange}
                                            >
                                                <span className="visually-hidden">{'Delivery tooltip'}</span>
                                                <div className="popover__tooltip">
                                                    {isWhite ? <InfoTooltipSvgWhite /> : <InfoTooltipSvg />}
                                                </div>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover__message">
                                                {'Must be the primary phone number on the account'}
                                                <PopoverClose />
                                                <PopoverArrow className="popover__arrow" />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                                {fieldState.error && (
                                    <div className="mt-1 text-xs text-[#df0c0c]">{fieldState.error.message}</div>
                                )}
                            </>
                        )}
                    />
                </div>
                {/* --------- email address --------- */}
                <div className="relative z-0 mb-6">
                    <input
                        type="text"
                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 pb-0.5 pt-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                        placeholder=" "
                        maxLength={50}
                        data-error={getFieldState('email').error ? 'true' : 'false'}
                        // data-empty={addressWatch === '' ? 'true' : 'false'}
                        tabIndex={0}
                        {...register('email')}
                        data-qm-encrypt="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-sm font-light duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                        htmlFor="email"
                    >
                        Email address*
                    </label>
                    {errors.email && <div className="mt-1 text-xs text-[#df0c0c]">{errors.email.message}</div>}
                </div>
                {/* --------- newsletter toggle --------- */}
                <div className="tscform__checkbox--wrap mb-5 mt-7 flex justify-items-start pb-6">
                    <Controller
                        name="subscribe"
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <Checkbox.Root
                                    checked={field.value}
                                    //defaultChecked={selectedCreditCard?.IsDefault}
                                    onCheckedChange={(checked) => {
                                        field.onChange(checked);
                                        if (checked) {
                                            const subscribeEvent = new CustomEvent('subscribe', {
                                                detail: {
                                                    Source: 'CreatePhoneAccount',
                                                },
                                            });
                                            document.dispatchEvent(subscribeEvent);
                                        }
                                    }}
                                    className={`tscform__checkbox peer flex h-5 w-5 flex-shrink-0 cursor-pointer border border-solid border-black shadow-[inset_0_0_0_3px_#fff] transition-all duration-300 ease-in-out before:content-[''] disabled:cursor-default data-[state=checked]:bg-black data-[state=checked]:disabled:border-[#a3a3a3] data-[state=checked]:disabled:bg-[#a3a3a3]`}
                                    id="c2"
                                ></Checkbox.Root>
                            </>
                        )}
                    />
                    <label
                        className="mb-0 cursor-pointer select-none pl-2.5 text-sm font-light peer-disabled:cursor-default"
                        htmlFor="subscribe"
                    >
                        Sign me up for Special Offers & Today&apos;s Showstopper Emails. I understand that I may
                        withdraw my consent at any time.{' '}
                        <a
                            target="_blank"
                            href="/go/aboutus/servicespage#privacypolicy"
                            className="underline hover:underline"
                        >
                            Our Privacy Policy
                        </a>
                    </label>
                </div>
                {/* --------- new password --------- */}
                <div className="mb-2">
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type={showNewPassword ? 'text' : 'password'}
                            maxLength={16}
                            placeholder=" "
                            data-error={getFieldState('newPassword').error ? 'true' : 'false'}
                            // data-empty={inputNew === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="new-password"
                            {...register('newPassword', {
                                onChange: (e) => {
                                    if (passw != '') {
                                        trigger('retypeNewPassword');
                                    }
                                },
                            })}
                            data-qm-block="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-sm font-light duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="newPassword"
                        >
                            Enter password*
                        </label>

                        <button
                            className="absolute bottom-0 right-0 z-[2]"
                            type="button"
                            onClick={toggleShowNewPassword}
                            tabIndex={-1}
                        >
                            {showNewPassword ? (
                                <ViewPasswordSvg width="20" height="19" />
                            ) : (
                                <HidePasswordSvg width="20" height="20" />
                            )}
                        </button>
                    </div>

                    {(getFieldState('newPassword').isTouched || getFieldState('newPassword').isDirty) && (
                        <div className="px-5 pb-7 pt-0 text-[13px]">
                            <div className="font-semibold">Your password must have:</div>
                            <div className="flex flex-col">
                                <div
                                    className={
                                        isValidTooSmall(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">at least 8 characters</span>
                                </div>
                                <div
                                    className={
                                        isValidUpper(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 upper case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidLower(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 lower case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidContainNumber(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 number</span>
                                </div>
                                <div
                                    className={
                                        isValidSpecial(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">
                                        include at least 1 special character
                                        <span className="max-md:hidden md:inline">
                                            {' '}
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                        <span className="max-md:block max-md:pl-6 md:hidden">
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* --------- retype password --------- */}
                    <div className="relative z-0 mb-6 mt-6 flex flex-col justify-center">
                        <div className="relative z-0 flex flex-col justify-center">
                            <input
                                className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                                type={showRetypePassword ? 'text' : 'password'}
                                // data-empty={inputRetype === '' ? 'true' : 'false'}
                                data-error={getFieldState('retypeNewPassword').error ? 'true' : 'false'}
                                tabIndex={0}
                                placeholder=" "
                                maxLength={16}
                                autoComplete="new-password"
                                {...register('retypeNewPassword')}
                                data-qm-block="true"
                            />
                            <label
                                className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-sm font-light duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                htmlFor="retypeNewPassword"
                            >
                                Re-type password*
                            </label>

                            <button
                                className="absolute bottom-0 right-0 z-[2]"
                                type="button"
                                onClick={toggleShowRetypePassword}
                                tabIndex={-1}
                            >
                                {showRetypePassword ? (
                                    <ViewPasswordSvg width="20" height="19" />
                                ) : (
                                    <HidePasswordSvg width="20" height="20" />
                                )}
                            </button>
                        </div>
                        {errors.retypeNewPassword && (
                            <div className="mt-1 font-semibold text-[#df0c0c]">{errors.retypeNewPassword.message}</div>
                        )}

                        {/* {mutation.isError ? <div className="profile__error">{mutation.error}</div> : null} */}
                    </div>
                </div>
                <div className="relative z-0 mb-6">
                    <p className="text-sm font-light">
                        By clicking &quot;Transfer account&quot; below, I acknowledge and agree that the information I
                        am providing will be subject to the TSC privacy policy, and I confirm my acceptance of the TSC
                        Terms of Use.
                    </p>
                </div>
                {/* --------- Submit --------- */}
                <div>
                    <div className="mb-7 mt-4 grid grid-cols-1">
                        <Button
                            type="submit"
                            aria-label={'Transfer account'}
                            disabled={!isValid || mutation.isLoading || hasSubmitted}
                            tabIndex={!isValid ? -1 : 0}
                        >
                            Transfer account
                        </Button>
                    </div>
                </div>
                <div className="text-left">
                    <a className="flex justify-start no-underline" href="/pages/signin">
                        <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            viewBox="0 0 45 45"
                        >
                            <g fill="none" fillRule="evenodd">
                                <path
                                    stroke="#000"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    className="chevron"
                                    d="M26 13 16 23l10 10"
                                />
                            </g>
                        </svg>
                        <span className="underline hover:underline">Back to Sign-in</span>
                    </a>
                </div>
                {mutation.isLoading || isMutating ? (
                    <div className="blockPageWrap" style={{ display: 'block' }}>
                        <div className="blockPageOverlay"></div>
                        <div className="blockMsg">
                            <LoadingSvg />
                        </div>
                    </div>
                ) : null}
            </form>
        </div>
    );
};
